import { useMemo } from 'react'
import type { Disposition, Signal, SignalTriage } from './signal-model'
import { useTriage } from './signal-store'
import { useSignals, type SignalsScope } from './signals'

/**
 * Per-detection disposition roll-up — the tuning loop on the Detections
 * registry (docs/signals-requirements.md §9 P3, decision S11). Pure fold
 * over the signals in a window and the local triage store; the registry
 * reads it as a column ("verdicts 30d") and a detail section.
 *
 * Keyed by the signal's workbook id (`Signal.detectionId`) — the same id
 * `DetectionRecord.id` carries for workbooks — with `detectionName` as the
 * fallback key for rows that carry no id (the stream shape). Lookups go
 * id-first, then name, via `dispositionFor`.
 */

export interface DetectionDispositions {
  /** Signals from this detection in the window. */
  total: number
  open: number
  closed: number
  /** true_positive. */
  tp: number
  /** benign_true_positive. */
  benign: number
  /** false_positive_logic — the rule itself is wrong. */
  fpLogic: number
  /** false_positive_data — the data was wrong. */
  fpData: number
  duplicate: number
  undetermined: number
  /** (fpLogic + fpData) / closed; null until something is closed. */
  fpRate: number | null
  /** The most recent close, for "last verdict by who, when". */
  lastDisposition: { disposition: Disposition; at: number; who: string } | null
}

/** FP-rate thresholds for the registry pill (decision S11). */
export const FP_RATE_HIGH = 0.5
export const FP_RATE_MEDIUM = 0.25
/** Below this many closed signals the rate is noise, not a verdict. */
export const FP_RATE_MIN_CLOSED = 3

function empty(): DetectionDispositions {
  return {
    total: 0, open: 0, closed: 0,
    tp: 0, benign: 0, fpLogic: 0, fpData: 0, duplicate: 0, undetermined: 0,
    fpRate: null, lastDisposition: null,
  }
}

/** The map key for a signal: workbook id when present, else the detection name. */
export function detectionKeyOf(s: Pick<Signal, 'detectionId' | 'detectionName'>): string | null {
  return s.detectionId || s.detectionName || null
}

/**
 * Fold signals + triage into one roll-up per detection key. Signals with
 * neither an id nor a name are skipped. A closed record without a
 * disposition (status set by hand) counts as closed but not in the rate.
 */
export function dispositionsByDetection(
  signals: Signal[],
  triage: Map<string, SignalTriage>,
): Map<string, DetectionDispositions> {
  const out = new Map<string, DetectionDispositions>()
  for (const s of signals) {
    const key = detectionKeyOf(s)
    if (!key) continue
    let d = out.get(key)
    if (!d) {
      d = empty()
      out.set(key, d)
    }
    d.total++
    const t = triage.get(s.uuid)
    if (t?.status !== 'closed') {
      d.open++
      continue
    }
    d.closed++
    switch (t.disposition) {
      case 'true_positive': d.tp++; break
      case 'benign_true_positive': d.benign++; break
      case 'false_positive_logic': d.fpLogic++; break
      case 'false_positive_data': d.fpData++; break
      case 'duplicate': d.duplicate++; break
      case 'undetermined': d.undetermined++; break
      case null: break
    }
    if (t.disposition && t.closedAt !== null && (d.lastDisposition === null || t.closedAt > d.lastDisposition.at)) {
      // The closer is the author of the last log line at/after closedAt;
      // fall back to the last log line at all.
      const closedAt = t.closedAt
      const who = [...t.log].reverse().find((e) => e.at >= closedAt)?.who ?? t.log[t.log.length - 1]?.who ?? ''
      d.lastDisposition = { disposition: t.disposition, at: closedAt, who }
    }
  }
  for (const d of out.values()) {
    d.fpRate = d.closed ? (d.fpLogic + d.fpData) / d.closed : null
  }
  return out
}

/** Registry lookup: id first, then name — mirrors how the key was chosen. */
export function dispositionFor(
  map: Map<string, DetectionDispositions>,
  d: { id: string; name: string },
): DetectionDispositions | null {
  return map.get(d.id) ?? map.get(d.name) ?? null
}

/** Pill tone for the FP rate; null = no pill (too few closed, or no rate). */
export function fpRateSeverity(d: Pick<DetectionDispositions, 'fpRate' | 'closed'> | null): 'high' | 'medium' | null {
  if (!d || d.fpRate === null || d.closed < FP_RATE_MIN_CLOSED) return null
  if (d.fpRate >= FP_RATE_HIGH) return 'high'
  if (d.fpRate >= FP_RATE_MEDIUM) return 'medium'
  return null
}

/** Compact `tp/fp` for the column cell: TP = true + benign, FP = logic + data. */
export function tpFpCounts(d: DetectionDispositions): { tp: number; fp: number } {
  return { tp: d.tp + d.benign, fp: d.fpLogic + d.fpData }
}

/* ----------------------------------------------------------------- React */

/**
 * The 30d roll-up for a scope: signals (demo/live via useSignals) joined
 * with the live local triage store. Re-computes on every triage mutation.
 * `enabled: false` skips the 30d signals fetch (the roll-up is then empty
 * and not pending).
 */
export function useDetectionDispositions(
  scope: SignalsScope,
  opts?: { enabled?: boolean },
): {
  dispositions: Map<string, DetectionDispositions>
  isPending: boolean
} {
  const enabled = opts?.enabled ?? true
  const signalsQ = useSignals(scope, '30d', { enabled })
  const { triage } = useTriage(scope.clusterId, scope.scopeId)
  const dispositions = useMemo(
    () => dispositionsByDetection(signalsQ.signals, triage),
    [signalsQ.signals, triage],
  )
  return { dispositions, isPending: enabled && signalsQ.isPending }
}
